// Home banner slider: the published slides from Admin → Hero slides sit side
// by side in a scroll-snap track, so without JavaScript they can still be
// swiped. This adds the arrows, the dots and autoplay. Autoplay waits while
// the mouse is over the banner, keyboard focus is in it, the tab is hidden
// or the banner is off screen; moving to a slide by hand starts its wait
// again. With reduced motion there is no autoplay.
const INTERVAL = 7000;

export function initSlider(root) {
  for (const el of root.querySelectorAll?.('[data-slider]:not([data-slider-init])') ?? []) {
    el.dataset.sliderInit = '';
    const track = el.querySelector('[data-slider-track]');
    const slides = track ? [...track.children] : [];
    if (slides.length > 1) setup(el, track, slides);
  }
}

function setup(el, track, slides) {
  const dots = [...el.querySelectorAll('[data-slider-dot]')];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let index = Math.round(track.scrollLeft / track.clientWidth) || 0;
  let hover = false;
  let focus = false;
  let inView = true;
  let timer = 0;

  const render = () => {
    slides.forEach((s, i) => { s.inert = i !== index; });
    dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
    // Screen readers hear a new slide only when it isn't moving on its own.
    track.setAttribute('aria-live', reduced || focus ? 'polite' : 'off');
  };
  const schedule = () => {
    clearTimeout(timer);
    if (!reduced && !hover && !focus && inView && !document.hidden) timer = setTimeout(() => show(index + 1), INTERVAL);
  };
  const show = (i) => {
    const n = (i + slides.length) % slides.length;
    track.scrollTo({ left: n * track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
  };

  let frame = 0;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      if (i === index || !slides[i]) return;
      index = i;
      render();
      schedule();
    });
  }, { passive: true });
  // Keep the current slide in place when the width changes.
  let width = track.clientWidth;
  new ResizeObserver(() => {
    if (track.clientWidth === width) return;
    width = track.clientWidth;
    track.scrollTo({ left: index * width, behavior: 'auto' });
  }).observe(track);

  el.querySelector('[data-slider-prev]')?.addEventListener('click', () => show(index - 1));
  el.querySelector('[data-slider-next]')?.addEventListener('click', () => show(index + 1));
  dots.forEach((d, i) => d.addEventListener('click', () => show(i)));
  // Keyboard focus holds the current slide; a mouse click on an arrow doesn't.
  el.addEventListener('focusin', (e) => {
    focus = e.target.matches(':focus-visible');
    render();
    schedule();
  });
  el.addEventListener('focusout', (e) => {
    if (el.contains(e.relatedTarget)) return;
    focus = false;
    render();
    schedule();
  });
  el.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hover = true; schedule(); } });
  el.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hover = false; schedule(); } });
  document.addEventListener('visibilitychange', schedule);
  new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; schedule(); }).observe(el);

  // The other slides' photos load once the page has, ready before they show.
  const eager = () => { for (const img of track.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager'; };
  if (document.readyState === 'complete') eager();
  else window.addEventListener('load', eager, { once: true });

  render();
  el.dataset.sliderReady = '';
  schedule();
}
